import type { NightmareType } from './nightmares.ts';
import { sec } from './units.ts';

/**
 * Battle plans (docs/TACTICAS.md): the shape of a wave and the moves its general may make. A plan splits each
 * sector's groups into squads with a role; the squads wait, escort or charge by order, and the general (the
 * heuristic in the simulation, or a decision model in a live room) moves the plan from phase to phase. The
 * budget is the same: a plan only decides when and where the nightmares go.
 */
export type PlanId = 'escortedSiege' | 'feint' | 'rush' | 'pincer';
export const PLAN_IDS: PlanId[] = ['escortedSiege', 'feint', 'rush', 'pincer'];

/**
 * engines: the siege engines, straight in. guard: stays by the engines and fights who comes near them.
 * main: the bulk, waiting out of the towers' reach until the plan sends it. wings: the flyers, held back the
 * same way. decoy: the loud ones that draw the heroes. second: a pincer's other half (it strikes the other flank).
 */
export type SquadRole = 'engines' | 'guard' | 'main' | 'wings' | 'decoy' | 'second';

/** What a squad is doing: walk in as ever, wait at its staging cell, stay by the engines, or storm its strike cell. */
export type SquadOrder = 'advance' | 'hold' | 'escort' | 'charge';

/**
 * A general's move, made per sector. `assault`: the bulk goes for its target (the breach, the gate or a flank).
 * `wings`: the flyers go in. `strike`: the bulk (and a pincer's second half, and the flyers) storms its target:
 * a flank, both flanks, or straight in. `shift`: the waiting bulk walks round outside to another sector and
 * waits there instead (it doesn't lead out of a phase; once per sector). `pulse`: a share of the waiting bulk
 * storms a target while the rest keeps waiting (it doesn't lead out of a phase either; the general's pressure).
 */
export type PlanMove = 'assault' | 'wings' | 'strike' | 'shift' | 'pulse';

/**
 * Where an assault or a strike goes, on the ground the bulk stands by: straight in by the gate, the wall a few
 * cells to the left or right of it, or a fallen stretch of wall.
 */
export type PlanTarget = 'gate' | 'left' | 'right' | 'breach';
export const PLAN_TARGETS: PlanTarget[] = ['gate', 'left', 'right', 'breach'];

export interface PlanDef {
  /** First Marea (index) it can come. */
  from: number;
  /** Draw weight against the other plans and against no plan at all (`PLAN.noneWeight`). */
  weight: number;
  /** Share of the wave's budget it spends: holding back and charging together is harder than a trickle. */
  budgetBp: number;
  /** Only when one of these kinds is in the wave's table. */
  needs?: NightmareType[];
  /** The waiting bulk may cross to another sector (a `shift`). */
  shift?: boolean;
  /**
   * The phases, in order, and the move that leads out of each (the last phase has none), with the targets it
   * may take (none: it has its own, as a pincer's two flanks). The heuristic takes a breach first, then the
   * first one no hero watches.
   */
  phases: { move: PlanMove | null; /** Ticks into the fight when the move is made anyway. */ by: number; targets?: PlanTarget[] }[];
}

export const PLANS: Record<PlanId, PlanDef> = {
  // Engines and their guard first; the bulk waits out of reach. A breach (or time) sends the bulk at it; once
  // the heroes are at the breach, the flyers.
  escortedSiege: {
    from: 2,
    weight: 2,
    budgetBp: 10000,
    needs: ['ram', 'mangonel'],
    // No shift: the bulk waits for the breach its engines make.
    phases: [
      { move: 'assault', by: sec(75), targets: ['breach', 'gate', 'left', 'right'] },
      { move: 'wings', by: sec(95) },
      { move: null, by: 0 },
    ],
  },
  // The loud ones charge the gate; when the heroes are on them, the bulk storms the wall a few cells aside.
  feint: {
    from: 2,
    weight: 2,
    budgetBp: 10000,
    shift: true,
    // Its own flank first (the one drawn for the wave), then the other, then the gate the decoy hit.
    phases: [
      { move: 'strike', by: sec(50), targets: ['left', 'right', 'gate', 'breach'] },
      { move: null, by: 0 },
    ],
  },
  // Everything by the paths gathers out of reach and charges at once (the wild still harries meanwhile).
  rush: {
    from: 2,
    weight: 2,
    // At the full budget: on Wardens' bastion (ghosts) the keep took 21 % against 8 % with no plan; campaign-4p
    // fell from 70 % to 61 %. Between the two (see DECISIONS).
    budgetBp: 8500,
    shift: true,
    phases: [
      { move: 'strike', by: sec(70), targets: ['gate', 'left', 'right', 'breach'] },
      { move: null, by: 0 },
    ],
  },
  // The bulk in two halves that storm the wall on both sides of the gate at once.
  pincer: {
    from: 3,
    weight: 2,
    // At the full budget: on Wardens' bastion (ghosts) 27 structures fell against 13 with no plan (even at 85 %);
    // campaign-4p fell from 70 % to 52 %. Between the two (see DECISIONS).
    budgetBp: 7000,
    phases: [
      { move: 'strike', by: sec(65) },
      { move: null, by: 0 },
    ],
  },
};

export const PLAN = {
  /** Weight of a wave with no plan, against PLANS' weights. */
  noneWeight: 4,
  /** The general looks at the field this often (ticks). */
  every: 12,
  /** Kinds that make up an engines' guard (the tough ones on foot). */
  guardKinds: ['skeleton', 'ogre', 'troll', 'beetle', 'bannerman', 'giant'] as NightmareType[],
  /** Share of the ground groups (not engines) that guard the engines. */
  guardShareBp: 3500,
  /** The opening squads (engines, guard, decoy) all come within this share of the spawn window. */
  openingBp: 4000,
  /** Share of the ground groups that make the decoy of a feint. */
  decoyShareBp: 3500,
  /** The bulk's staging cell keeps this far (fixed) from every tower. */
  stageClearance: 7500,
  /** A guard stays this close to its engine and goes after a hero this close to it. */
  guardLeash: 1400,
  guardRadius: 3200,
  /** Heroes this close to the decoy are on the bait; this close to the strike cell, they saw it coming. */
  baitRadius: 3500,
  watchRadius: 3000,
  /** After the assault, the flyers go in once a hero is at the breach, or this long after anyway (ticks). */
  wingsAfter: sec(12),
  /** A pincer strikes when the alive heroes all stand within this of one another (one flank covered at most). */
  bunchRadius: 3500,
  /** A rush goes when no hero stands within this of the sector's gate. */
  awayRadius: 6000,
  /**
   * The heuristic shifts the waiting bulk when this share of the alive heroes stand within `awayRadius` of its
   * gate and none of another sector's.
   */
  shiftShareBp: 7500,
  /** The general can send the bulk (assault, strike) once this share of it has come out; time sends it anyway. */
  readyBp: 5000,
  /** Wall spots a live general may aim at, evenly round the ring just outside the wall (besides gate and flanks). */
  spots: 8,
  /** A pulse sends this share of the bulk waiting in the sector (rounded up), at most once this often (ticks). */
  pulseShareBp: 3500,
  pulseEvery: sec(8),
  /** Fewer than this waiting, no pulse: the rest is the strike. */
  pulseMin: 6,
  /** The heuristic doesn't shift with less than this left before the phase's move is made anyway (ticks). */
  shiftLead: sec(30),
  /** A shifting nightmare has reached a waypoint of its way round when this close to it. */
  wayReach: 700,
  /** The way round keeps at least this many cells out past the bastion's wall. */
  wayOut: 3,
  /** Half the alive heroes out past the wall near the engines: the gate is bare, the bulk goes at once. */
  sortieShareBp: 5000,
  /** Out past the wall: no walled cell within this many cells (a hero fighting at the gate isn't out). */
  sortieCells: 2,
  /** A hero out past the wall this close to a waiting nightmare sets its sector off: the move is made at once. */
  provokeRadius: 3500,
};

/**
 * The army a general drafts for a wave (docs/TACTICAS.md): how many of each kind, bought from the Marea's table
 * with the wave's budget (all sectors' together). The limits keep it a strategy and not one kind spammed.
 */
export const ARMY = {
  /** No kind takes more than this share of the budget. */
  maxKindBp: 3000,
  /** At least this many kinds. */
  minKinds: 4,
  /** No sector gets more than this share of the budget (with two or more): it may lean on one, not empty another. */
  maxSectorBp: 6500,
  /**
   * Share of the wave's budget a drafted army gets: the general picks it by simulating candidates against the
   * bastion, so it hurts far more per point than a draw (calibrated with campaign-4p, see DECISIONS).
   */
  budgetBp: 10000,
  /** The general drafts the waves that bring a plan, from this Marea on. */
  fromMarea: 2,
  /** Armies the general tries per wave, by Marea (the last one for any later): it thinks harder, not bigger. */
  candidates: [0, 0, 4, 6, 8, 10],
  /** It starts once the wave before is over, and never earlier than this before the wave (ms). */
  leadMs: 12 * 3_600_000,
  /** Candidates it tries per wake-up, and how often it wakes while it drafts (ms): the object never stalls long. */
  perStep: 1,
  stepMs: 60_000,
  /** A candidate that changes its best so far moves this share of one kind's points to another. */
  mutateBp: 3500,
  /**
   * The omen's hint at a drafted army: what most of its points went to (fliers, siege, shooters, a swarm of
   * small ones, heavy ones), or a mixed army when nothing reaches this share.
   */
  hintShareBp: 3500,
  hintSwarmPoints: 4,
  hintHeavyPoints: 28,
};
